import {
  AddonFragment,
  CustomerFragment,
  EntitlementFragment,
  GetPaywallQuery,
  PackageEntitlementFragment,
  PlanFragment,
  PriceFragment,
  PromotionalEntitlementFragment,
  SubscriptionFragment,
  TotalPriceFragment,
  SubscriptionPreviewFragment,
  AccessDeniedReason as AccessDeniedReasonDTO,
  CouponFragment,
  EntitlementCheckResult,
  EntitlementResetPeriod as ApiEntitlementResetPeriod,
  FeatureType as ApiFeatureType,
  GetCouponsQuery,
  GetMockPaywallQuery,
  MeterType,
  MonthlyAccordingTo,
  MonthlyResetPeriodConfigFragment,
  MonthlyResetPeriodConfigInput,
  MockPaywallAddonFragment,
  MockPaywallPackageEntitlementFragment,
  MockPaywallPlanFragment,
  MockPaywallPriceFragment,
  PricingType,
  ResetPeriodConfigurationFragment,
  WeeklyAccordingTo,
  WeeklyResetPeriodConfigFragment,
  WeeklyResetPeriodConfigInput,
  CustomerResourceFragment,
  CustomerPortalFragment,
} from '@stigg/api-client-js/src/generated/sdk';
import {
  AccessDeniedReason,
  Addon,
  BooleanEntitlement,
  BooleanEntitlementFallback,
  Coupon,
  Currency,
  Customer,
  CustomerModel,
  CustomerPortal,
  CustomerResource,
  EntitlementFeature,
  EntitlementResetPeriod,
  FeatureType,
  MeteredEntitlement,
  MeteredEntitlementFallback,
  Money,
  NumericEntitlement,
  NumericEntitlementFallback,
  PackageEntitlement,
  PaymentMethodDetails,
  Paywall,
  Plan,
  Price,
  PromotionalEntitlement,
  ResetSettings,
  Subscription,
  SubscriptionPreview,
  SubscriptionPreviewDiscount,
  SubscriptionPrice,
} from '../models';
import map from 'lodash/map';
import CachedEntitlement from '../services/cachedEntitlement';
import { Decision } from '../services/entitlementDecisionService';
import omit from 'lodash/omit';
import some from 'lodash/some';
import { buildCacheKeyFromEntitlement } from './cacheKeysHelpers';

export class ModelMapper {
  mapBooleanEntitlement(entitlement: CachedEntitlement, decision: Decision): BooleanEntitlement {
    return {
      isFallback: false,
      hasAccess: decision.hasAccess,
      accessDeniedReason: decision.accessDeniedReason,
      feature: entitlement.calculatedEntitlement.feature
        ? {
            ...entitlement.calculatedEntitlement.feature,
            isMetered: false,
          }
        : undefined,
    };
  }

  mapNumericEntitlement(entitlement: CachedEntitlement, decision: Decision): NumericEntitlement {
    const { hasUnlimitedUsage, usageLimit } = entitlement.calculatedEntitlement;
    return {
      isFallback: false,
      hasAccess: decision.hasAccess,
      accessDeniedReason: decision.accessDeniedReason,
      isUnlimited: hasUnlimitedUsage,
      value: usageLimit || undefined,
      feature: entitlement.calculatedEntitlement.feature
        ? {
            ...entitlement.calculatedEntitlement.feature,
            isMetered: false,
          }
        : undefined,
    };
  }

  mapMeteredEntitlement(
    entitlement: CachedEntitlement,
    decision: Decision,
    requestedUsage?: number,
  ): MeteredEntitlement {
    const { hasUnlimitedUsage, usageLimit, feature } = entitlement.calculatedEntitlement;
    const { currentUsage, nextResetDate, resetPeriod } = entitlement.featureUsage;
    let resetSettings: ResetSettings | null = null;
    if (
      feature &&
      feature.featureType === FeatureType.Numeric &&
      feature.meterType === MeterType.Incremental &&
      resetPeriod
    ) {
      resetSettings = {
        nextResetDate,
        resetPeriod,
      };
    }

    return {
      isFallback: false,
      hasAccess: decision.hasAccess,
      accessDeniedReason: decision.accessDeniedReason,
      isUnlimited: hasUnlimitedUsage,
      usageLimit: usageLimit || undefined,
      currentUsage: currentUsage,
      requestedUsage: requestedUsage || 0,
      resetSettings: resetSettings || undefined,
      feature: entitlement.calculatedEntitlement.feature
        ? {
            ...entitlement.calculatedEntitlement.feature,
            meterType: entitlement.calculatedEntitlement.feature.meterType || undefined,
            isMetered: true,
          }
        : undefined,
    };
  }

  mapEntitlement(entitlement: CachedEntitlement, decision: Decision, requestedUsage?: number) {
    const { calculatedEntitlement } = entitlement;
    if (!calculatedEntitlement.feature) {
      return this.mapBooleanEntitlement(entitlement, decision);
    }
    const { featureType, meterType } = calculatedEntitlement.feature;
    const isMetered =
      featureType === FeatureType.Numeric &&
      (meterType === MeterType.Incremental || meterType === MeterType.Fluctuating);

    switch (featureType) {
      case FeatureType.Boolean:
        return this.mapBooleanEntitlement(entitlement, decision);
      case FeatureType.Numeric: {
        return isMetered
          ? this.mapMeteredEntitlement(entitlement, decision, requestedUsage)
          : this.mapNumericEntitlement(entitlement, decision);
      }
    }
  }

  mapFeatureType(featureType: ApiFeatureType): FeatureType {
    switch (featureType) {
      case ApiFeatureType.Boolean:
        return FeatureType.Boolean;
      case ApiFeatureType.Number:
        return FeatureType.Numeric;
    }
  }

  mapEntitlementResetPeriod(resetPeriod: ApiEntitlementResetPeriod): EntitlementResetPeriod {
    switch (resetPeriod) {
      case ApiEntitlementResetPeriod.Day:
        return EntitlementResetPeriod.Daily;
      case ApiEntitlementResetPeriod.Hour:
        return EntitlementResetPeriod.Hourly;
      case ApiEntitlementResetPeriod.Week:
        return EntitlementResetPeriod.Weekly;
      case ApiEntitlementResetPeriod.Month:
        return EntitlementResetPeriod.Monthly;
    }
  }

  mapApiEntitlementResetPeriod(resetPeriod: EntitlementResetPeriod): ApiEntitlementResetPeriod {
    switch (resetPeriod) {
      case EntitlementResetPeriod.Daily:
        return ApiEntitlementResetPeriod.Day;
      case EntitlementResetPeriod.Hourly:
        return ApiEntitlementResetPeriod.Hour;
      case EntitlementResetPeriod.Weekly:
        return ApiEntitlementResetPeriod.Week;
      case EntitlementResetPeriod.Monthly:
        return ApiEntitlementResetPeriod.Month;
    }
  }

  mapCachedEntitlement(
    entitlement: EntitlementFragment,
    currentUsage?: number,
    nextResetDate?: number | null,
  ): CachedEntitlement {
    let feature: EntitlementFeature | undefined;
    if (entitlement.feature) {
      feature = {
        id: entitlement.feature.refId,
        featureType: this.mapFeatureType(entitlement.feature.featureType),
        meterType: entitlement.feature.meterType || undefined,
        units: entitlement.feature.featureUnits || undefined,
        unitsPlural: entitlement.feature.featureUnitsPlural || undefined,
        displayName: entitlement.feature.displayName,
        description: entitlement.feature.description || '',
      };
    }
    return {
      calculatedEntitlement: {
        usageLimit: entitlement.usageLimit,
        hasUnlimitedUsage: entitlement.hasUnlimitedUsage,
        feature,
      },
      featureUsage: {
        resetPeriod: entitlement.resetPeriod ? this.mapEntitlementResetPeriod(entitlement.resetPeriod) : undefined,
        resetPeriodConfiguration: entitlement.resetPeriodConfiguration,
        currentUsage: currentUsage || entitlement.currentUsage || 0,
        nextResetDate: nextResetDate || entitlement.nextResetDate,
      },
    };
  }

  mapCachedEntitlements(updatedEntitlements: EntitlementFragment[]): Map<string, CachedEntitlement> {
    return new Map(
      updatedEntitlements.map((entitlement) => [
        buildCacheKeyFromEntitlement(entitlement),
        this.mapCachedEntitlement(entitlement),
      ]),
    );
  }

  mapPlans(resultData: GetPaywallQuery): Plan[] {
    const plans = resultData?.paywall.plans || [];
    return plans.map((plan, index) => this.mapPlan(plan, index));
  }

  mapPaywall(resultData: GetPaywallQuery): Paywall {
    const mappedPlans = this.mapPlans(resultData);
    const configuration = resultData?.paywall.configuration;

    const customer = resultData?.paywall.customer ? this.mapCustomer(resultData?.paywall.customer) : null;
    const resource = resultData?.paywall.resource ? this.mapResource(resultData?.paywall.resource) : null;

    return {
      plans: mappedPlans,
      configuration,
      customer,
      resource,
      currency: resultData.paywall.currency,
    };
  }

  mapMockPlans(resultData: GetMockPaywallQuery): Paywall {
    const plans = resultData?.mockPaywall.plans || [];
    return {
      plans: plans.map((plan, index) => this.mapPlan(plan, index)),
      configuration: resultData.mockPaywall.configuration,
      customer: null,
      resource: null,
      currency: { code: Currency.Usd, symbol: '$' },
    };
  }

  mapCoupons(resultData: GetCouponsQuery): Coupon[] {
    return resultData.coupons.edges.map((x) => x.node).map((coupon) => this.mapCoupon(coupon));
  }

  mapResource(resource: CustomerResourceFragment): CustomerResource {
    return {
      id: resource.resourceId,
    };
  }

  mapCustomerPortal(customerPortal: CustomerPortalFragment): CustomerPortal {
    const resource = customerPortal.resource ? this.mapResource(customerPortal.resource) : null;

    return {
      ...customerPortal,
      resource,
    };
  }

  mapCustomer(customer: CustomerFragment): Customer {
    return new CustomerModel({
      id: customer.refId,
      name: customer.name || undefined,
      email: customer.email || undefined,
      createdAt: new Date(customer.createdAt),
      updatedAt: new Date(customer.updatedAt),
      hasPaymentMethod: customer.hasPaymentMethod,
      subscriptions: map(customer.subscriptions, (x) => this.mapSubscription(x)),
      promotionalEntitlements: map(customer.promotionalEntitlements, (x) => this.mapPromotionalEntitlement(x)),
      metadata: customer.additionalMetaData,
      paymentMethodDetails: this.mapPaymentDetails(customer),
      trialedPlans:
        customer.trialedPlans?.map((trialedPlan) => ({
          productId: trialedPlan.productRefId,
          planId: trialedPlan.planRefId,
        })) || undefined,
      eligibleForTrial: customer.eligibleForTrial?.map((eligibleForTrial) => ({
        productId: eligibleForTrial.productRefId,
        eligible: eligibleForTrial.eligible,
      })),
      experimentInfo: omit(customer.experimentInfo, '__typename'),
    });
  }

  private mapPaymentDetails(customer: CustomerFragment): PaymentMethodDetails | undefined {
    const { defaultPaymentExpirationYear, defaultPaymentExpirationMonth, defaultPaymentMethodLast4Digits } = customer;
    if (
      some([defaultPaymentMethodLast4Digits, defaultPaymentExpirationMonth, defaultPaymentExpirationYear], (x) => x)
    ) {
      return {
        expirationMonth: defaultPaymentExpirationMonth,
        expirationYear: defaultPaymentExpirationYear,
        last4Digits: defaultPaymentMethodLast4Digits,
      };
    }

    return undefined;
  }

  private mapTotalPrice(
    pricing: PriceFragment,
    totalPrice: TotalPriceFragment,
    usageLimit?: number | null,
  ): SubscriptionPrice {
    const { feature, price, billingModel, billingPeriod } = pricing;
    const mappedPrice: SubscriptionPrice = {
      pricingModel: billingModel,
      billingPeriod,
      amount: totalPrice.total.amount,
      grossAmount: totalPrice.subTotal.amount,
      currency: price.currency,
    };
    if (feature) {
      mappedPrice.feature = {
        displayName: feature.displayName,
        units: feature.featureUnits,
        unitsPlural: feature.featureUnitsPlural,
        unitQuantity: usageLimit,
      };
    }

    return mappedPrice;
  }

  private mapPrice(pricing: PriceFragment | MockPaywallPriceFragment, usageLimit?: number | null): Price {
    const {
      feature,
      price,
      billingId,
      billingModel,
      billingPeriod,
      minUnitQuantity,
      maxUnitQuantity,
      billingCountryCode,
    } = pricing;
    const mappedPrice: Price = {
      pricingModel: billingModel,
      billingPeriod,
      billingId,
      amount: price.amount,
      currency: price.currency,
      minUnitQuantity,
      maxUnitQuantity,
      billingCountryCode,
    };
    if (feature) {
      mappedPrice.feature = {
        displayName: feature.displayName,
        units: feature.featureUnits,
        unitsPlural: feature.featureUnitsPlural,
        unitQuantity: usageLimit,
      };
    }

    return mappedPrice;
  }

  private mapPlan(
    plan: (PlanFragment | MockPaywallPlanFragment) & {
      compatibleAddons?: AddonFragment[] | MockPaywallAddonFragment[] | null;
    },
    order = 0,
  ): Plan {
    return {
      id: plan.refId,
      order,
      displayName: plan.displayName,
      description: plan.description,
      billingId: plan.billingId,
      basePlan: plan.basePlan ? { id: plan.basePlan.refId, displayName: plan.basePlan.displayName } : undefined,
      entitlements: map(plan.entitlements, (entitlement) =>
        this.mapPackageEntitlement(entitlement as PackageEntitlementFragment | EntitlementFragment),
      ),
      inheritedEntitlements: map(plan.inheritedEntitlements, (entitlement) =>
        this.mapPackageEntitlement(entitlement as PackageEntitlementFragment | EntitlementFragment),
      ),
      pricePoints: map(plan.prices, (price) => this.mapPrice(price as PriceFragment | MockPaywallPriceFragment)),
      pricingType: plan.pricingType,
      defaultTrialConfig: plan.defaultTrialConfig,
      compatibleAddons: this.mapCompatibleAddons(plan),
      product: {
        id: plan.product.refId,
        displayName: plan.product.displayName,
        description: plan.product.description,
        metadata: plan.product.additionalMetaData,
      },
      metadata: plan.additionalMetaData,
    };
  }

  private mapCompatibleAddons(
    plan: (PlanFragment | MockPaywallPlanFragment) & {
      compatibleAddons?: (AddonFragment | MockPaywallAddonFragment)[] | null;
    },
  ) {
    if (plan.pricingType === PricingType.Free || !plan.compatibleAddons) {
      return [];
    }

    const { compatibleAddons } = plan;

    const filteredCompatibleAddons =
      plan.pricingType === PricingType.Paid
        ? compatibleAddons.filter((addon) => addon.pricingType === PricingType.Paid)
        : compatibleAddons;
    return filteredCompatibleAddons.map((addon: AddonFragment | MockPaywallAddonFragment) => this.mapAddon(addon));
  }

  private mapPackageEntitlement(
    entitlement: PackageEntitlementFragment | (MockPaywallPackageEntitlementFragment & { isCustom?: boolean }),
  ): PackageEntitlement {
    const { feature } = entitlement;
    if (!feature) {
      return {
        usageLimit: entitlement.usageLimit || 0,
        hasUnlimitedUsage: entitlement.hasUnlimitedUsage,
        resetPeriod: entitlement.resetPeriod ? this.mapEntitlementResetPeriod(entitlement.resetPeriod) : undefined,
      };
    }
    return {
      usageLimit: entitlement.usageLimit || 0,
      feature: {
        id: feature.refId,
        featureType: this.mapFeatureType(feature.featureType),
        description: feature.description || '',
        meterType: feature.meterType || undefined,
        units: feature.featureUnits || undefined,
        unitsPlural: feature.featureUnitsPlural || undefined,
        displayName: feature.displayName,
        isMetered: feature.meterType === MeterType.Fluctuating || feature.meterType === MeterType.Incremental,
        metadata: feature.additionalMetaData,
      },
      isCustom: entitlement.isCustom || undefined,
      hasUnlimitedUsage: entitlement.hasUnlimitedUsage,
      resetPeriod: entitlement.resetPeriod ? this.mapEntitlementResetPeriod(entitlement.resetPeriod) : undefined,
      hiddenFromWidgets: entitlement.hiddenFromWidgets,
      displayNameOverride: entitlement.displayNameOverride,
    };
  }

  private mapPromotionalEntitlement(entitlement: PromotionalEntitlementFragment): PromotionalEntitlement {
    const { feature } = entitlement;
    return {
      status: entitlement.status,
      usageLimit: entitlement.usageLimit || 0,
      feature: {
        id: feature.refId,
        displayName: feature.displayName,
        description: feature.description || '',
        featureType: this.mapFeatureType(feature.featureType),
        meterType: feature.meterType || undefined,
        units: feature.featureUnits || undefined,
        unitsPlural: feature.featureUnitsPlural || undefined,
        isMetered: feature.meterType === MeterType.Fluctuating || feature.meterType === MeterType.Incremental,
      },
      hasUnlimitedUsage: entitlement.hasUnlimitedUsage,
      expiresAt: entitlement.endDate,
      hasExpirationDate: !!entitlement.endDate,
      isVisible: entitlement.isVisible,
    };
  }

  private mapSubscription(subscription: SubscriptionFragment): Subscription {
    const { totalPrice } = subscription;
    const [planPrice] = subscription.prices || [];
    return {
      id: subscription.refId,
      resourceId: subscription.resource?.resourceId,
      status: subscription.status,
      currentBillingPeriodEnd: this.mapDate(subscription.currentBillingPeriodEnd),
      plan: this.mapPlan(subscription.plan),
      price:
        planPrice && totalPrice && planPrice.price
          ? this.mapTotalPrice(planPrice.price, totalPrice, planPrice.usageLimit)
          : null,
      pricingType: subscription.pricingType,
      addons:
        subscription.addons?.map(({ quantity, addon }) => ({
          quantity: quantity,
          addon: this.mapAddon(addon),
        })) || [],
      startDate: subscription.startDate,
      endDate: this.mapDate(subscription.endDate),
      trialEndDate: this.mapDate(subscription.trialEndDate),
      cancellationDate: this.mapDate(subscription.cancellationDate),
      effectiveEndDate: this.mapDate(subscription.effectiveEndDate),
      metadata: subscription.additionalMetaData,
      experimentInfo: omit(subscription.experimentInfo, '__typename'),
      scheduledUpdates: subscription.scheduledUpdates || [],
    };
  }

  private mapDate(date?: Date) {
    return date ? new Date(date) : undefined;
  }

  private mapAddon(addon: AddonFragment | MockPaywallAddonFragment): Addon {
    return {
      id: addon.refId,
      displayName: addon.displayName,
      description: addon.description || '',
      billingId: addon.billingId,
      entitlements: map(addon.entitlements, (x) =>
        this.mapPackageEntitlement(x as PackageEntitlementFragment | EntitlementFragment),
      ),
      pricePoints: map(addon.prices, (price) => this.mapPrice(price as PriceFragment | MockPaywallPriceFragment)),
      pricingType: addon.pricingType,
      metadata: addon.additionalMetaData,
    };
  }

  private mapAccessDeniedReason(accessDeniedReason: AccessDeniedReason): AccessDeniedReasonDTO {
    switch (accessDeniedReason) {
      case AccessDeniedReason.InvalidFeatureType:
        return AccessDeniedReasonDTO.Unknown;
      case AccessDeniedReason.CustomerNotFound:
        return AccessDeniedReasonDTO.CustomerNotFound;
      case AccessDeniedReason.FeatureNotFound:
        return AccessDeniedReasonDTO.FeatureNotFound;
      case AccessDeniedReason.NoActiveSubscriptions:
        return AccessDeniedReasonDTO.NoActiveSubscription;
      case AccessDeniedReason.CustomerNotEntitledForFeature:
        return AccessDeniedReasonDTO.NoFeatureEntitlementInSubscription;
      case AccessDeniedReason.RequestedUsageExceedsLimit:
        return AccessDeniedReasonDTO.RequestedUsageExceedingLimit;
      case AccessDeniedReason.Unknown:
        return AccessDeniedReasonDTO.Unknown;
    }
  }

  private getResetPeriodData = (
    resetPeriod?: EntitlementResetPeriod | null,
    resetPeriodConfiguration?: ResetPeriodConfigurationFragment | null,
  ): {
    weeklyResetPeriodConfiguration?: WeeklyResetPeriodConfigInput;
    monthlyResetPeriodConfiguration?: MonthlyResetPeriodConfigInput;
  } => {
    switch (resetPeriod) {
      case EntitlementResetPeriod.Monthly:
        const monthlyConfig = resetPeriodConfiguration as MonthlyResetPeriodConfigFragment;
        return {
          monthlyResetPeriodConfiguration: {
            accordingTo: monthlyConfig.monthlyAccordingTo || MonthlyAccordingTo.SubscriptionStart,
          },
        };
      case EntitlementResetPeriod.Weekly:
        const weeklyConfig = resetPeriodConfiguration as WeeklyResetPeriodConfigFragment;
        return {
          weeklyResetPeriodConfiguration: {
            accordingTo: weeklyConfig.weeklyAccordingTo || WeeklyAccordingTo.EveryFriday,
          },
        };
      default:
        return {};
    }
  };

  mapFallbackBooleanEntitlementResult(
    entitlement: BooleanEntitlementFallback,
    decision: Decision,
  ): EntitlementCheckResult {
    return {
      hasAccess: entitlement.hasAccess,
      accessDeniedReason: decision.accessDeniedReason
        ? this.mapAccessDeniedReason(decision.accessDeniedReason)
        : undefined,
    };
  }

  mapFallbackNumericEntitlementResult(
    entitlement: NumericEntitlementFallback,
    decision: Decision,
  ): EntitlementCheckResult {
    return {
      hasAccess: entitlement.hasAccess,
      accessDeniedReason: decision.accessDeniedReason
        ? this.mapAccessDeniedReason(decision.accessDeniedReason)
        : undefined,
      usageLimit: entitlement.value,
      hasUnlimitedUsage: entitlement.isUnlimited,
    };
  }

  mapEntitlementResult(
    decision: Decision,
    entitlement?: CachedEntitlement,
    requestedUsage?: number,
  ): EntitlementCheckResult {
    return {
      hasAccess: decision.hasAccess,
      accessDeniedReason: decision.accessDeniedReason
        ? this.mapAccessDeniedReason(decision.accessDeniedReason)
        : undefined,
      hasUnlimitedUsage: entitlement?.calculatedEntitlement.hasUnlimitedUsage,
      usageLimit: entitlement?.calculatedEntitlement.usageLimit,
      currentUsage: entitlement?.featureUsage.currentUsage,
      requestedUsage,
      nextResetDate: entitlement?.featureUsage.nextResetDate,
      resetPeriod: entitlement?.featureUsage.resetPeriod
        ? this.mapApiEntitlementResetPeriod(entitlement.featureUsage.resetPeriod)
        : undefined,
      ...this.getResetPeriodData(
        entitlement?.featureUsage.resetPeriod,
        entitlement?.featureUsage.resetPeriodConfiguration,
      ),
    };
  }

  mapFallbackMeteredEntitlementResult(
    entitlement: MeteredEntitlementFallback,
    decision: Decision,
    requestedUsage?: number,
  ): EntitlementCheckResult {
    return {
      hasAccess: entitlement.hasAccess,
      accessDeniedReason: decision.accessDeniedReason
        ? this.mapAccessDeniedReason(decision.accessDeniedReason)
        : undefined,
      hasUnlimitedUsage: entitlement.isUnlimited,
      usageLimit: entitlement.usageLimit,
      requestedUsage,
    };
  }

  mapSubscriptionPreview(fragment: SubscriptionPreviewFragment): SubscriptionPreview {
    const { subscription, proration, billingPeriodRange } = fragment;
    return {
      total: this.mapMoney(fragment.total),
      subTotal: this.mapMoney(fragment.subTotal),
      ...(fragment.discount ? { discount: this.mapDiscount(fragment.discount) } : {}),
      billingPeriodRange: {
        start: billingPeriodRange.start && new Date(billingPeriodRange.start),
        end: billingPeriodRange.end && new Date(billingPeriodRange.end),
      },
      proration: proration
        ? {
            credit: this.mapMoney(proration.credit),
            debit: this.mapMoney(proration.debit),
            netAmount: this.mapMoney(proration.netAmount),
            prorationDate: new Date(proration.prorationDate),
          }
        : undefined,
      subscription: subscription
        ? {
            total: this.mapMoney(subscription.total),
            subTotal: this.mapMoney(subscription.subTotal),
          }
        : undefined,
    };
  }

  mapDiscount(discount: NonNullable<SubscriptionPreviewFragment['discount']>): SubscriptionPreviewDiscount {
    const { type, value, durationType, durationInMonths } = discount;
    return { type, value, durationType, ...(durationInMonths ? { durationInMonths } : {}) };
  }

  mapMoney(fragment: { amount: number; currency: string }): Money {
    return {
      amount: fragment.amount,
      currency: fragment.currency,
    };
  }

  private mapCoupon(coupon: CouponFragment): Coupon {
    return {
      id: coupon.refId,
      name: coupon.name,
      description: coupon.description,
      metadata: coupon.additionalMetaData,
      discountValue: coupon.discountValue,
    };
  }
}
